import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";

import { PortalShell } from "../components/PortalShell";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { ApiError } from "../lib/api";
import {
  WEBHOOK_EVENT_TYPES,
  createWebhook,
  deleteWebhook,
  listWebhooks,
  testWebhook,
  type CreatedWebhook,
  type Webhook,
  type WebhookEventType,
} from "../lib/webhooks";

export function WebhooksPage(): JSX.Element {
  const queryClient = useQueryClient();
  const { data, isLoading, error } = useQuery({
    queryKey: ["webhooks"],
    queryFn: listWebhooks,
  });

  const [showForm, setShowForm] = useState(false);
  const [created, setCreated] = useState<CreatedWebhook | null>(null);
  const items = Array.isArray(data) ? data : [];

  const deleteMutation = useMutation({
    mutationFn: deleteWebhook,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["webhooks"] }),
  });
  const testMutation = useMutation({ mutationFn: testWebhook });

  return (
    <PortalShell>
      <header className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div className="space-y-1">
          <h1 className="text-2xl font-semibold text-neutral-900">Webhooks</h1>
          <p className="text-sm text-neutral-600">
            Subscribe to template + AI events. Each delivery is HMAC-SHA256 signed.
          </p>
        </div>
        {!showForm && created === null && (
          <Button variant="primary" onClick={() => setShowForm(true)}>
            + New webhook
          </Button>
        )}
      </header>

      {created !== null && (
        <CreatedWebhookBanner
          created={created}
          onClose={() => {
            setCreated(null);
            setShowForm(false);
            void queryClient.invalidateQueries({ queryKey: ["webhooks"] });
          }}
        />
      )}

      {showForm && created === null && (
        <CreateWebhookForm
          onCreated={(w) => setCreated(w)}
          onCancel={() => setShowForm(false)}
        />
      )}

      <section className="mt-6 rounded-lg border border-neutral-200 bg-white shadow-sm">
        {isLoading ? (
          <p className="px-6 py-12 text-sm text-neutral-500">Loading…</p>
        ) : error instanceof ApiError ? (
          <p
            role="alert"
            className="m-6 rounded-md border border-error-200 bg-error-50 px-3 py-2 text-sm text-error-700"
          >
            {error.problem.detail || error.problem.title}
          </p>
        ) : items.length === 0 ? (
          <p className="px-6 py-12 text-center text-sm text-neutral-500">
            No webhooks configured yet.
          </p>
        ) : (
          <ul className="divide-y divide-neutral-200">
            {items.map((webhook) => (
              <WebhookRow
                key={webhook.id}
                webhook={webhook}
                onDelete={() => deleteMutation.mutate(webhook.id)}
                onTest={() => testMutation.mutate(webhook.id)}
                deleting={deleteMutation.isPending && deleteMutation.variables === webhook.id}
                testing={testMutation.isPending && testMutation.variables === webhook.id}
              />
            ))}
          </ul>
        )}
      </section>
    </PortalShell>
  );
}

function WebhookRow({
  webhook,
  onDelete,
  onTest,
  deleting,
  testing,
}: {
  webhook: Webhook;
  onDelete: () => void;
  onTest: () => void;
  deleting: boolean;
  testing: boolean;
}): JSX.Element {
  return (
    <li className="flex flex-wrap items-center justify-between gap-3 px-6 py-4">
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium text-neutral-900">{webhook.url}</p>
        {webhook.description !== null && webhook.description !== undefined && (
          <p className="text-xs text-neutral-500">{webhook.description}</p>
        )}
        <p className="mt-1 flex flex-wrap gap-1 text-xs">
          {webhook.events.map((event) => (
            <span
              key={event}
              className="rounded-full bg-neutral-100 px-2 py-0.5 font-mono text-[11px] text-neutral-700"
            >
              {event}
            </span>
          ))}
        </p>
      </div>
      <div className="flex gap-2">
        <Button variant="outline" size="sm" loading={testing} onClick={onTest}>
          Send test
        </Button>
        <Button
          variant="destructive"
          size="sm"
          loading={deleting}
          onClick={() => {
            if (window.confirm(`Delete webhook to ${webhook.url}?`)) {
              onDelete();
            }
          }}
        >
          Delete
        </Button>
      </div>
    </li>
  );
}

function CreateWebhookForm({
  onCreated,
  onCancel,
}: {
  onCreated: (w: CreatedWebhook) => void;
  onCancel: () => void;
}): JSX.Element {
  const [url, setUrl] = useState("");
  const [description, setDescription] = useState("");
  const [events, setEvents] = useState<WebhookEventType[]>([]);
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: createWebhook,
    onSuccess: onCreated,
  });

  function toggleEvent(event: WebhookEventType): void {
    setEvents((prev) =>
      prev.includes(event) ? prev.filter((e) => e !== event) : [...prev, event],
    );
  }

  function onSubmit(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    setError(null);
    if (events.length === 0) {
      setError("Pick at least one event.");
      return;
    }
    mutation.mutate(
      {
        url,
        events,
        description: description === "" ? undefined : description,
      },
      {
        onError: (err) => {
          if (err instanceof ApiError) {
            setError(err.problem.detail || err.problem.title);
          } else {
            setError("Couldn't create webhook. Please try again.");
          }
        },
      },
    );
  }

  return (
    <form
      onSubmit={onSubmit}
      className="space-y-4 rounded-lg border border-neutral-200 bg-white p-6 shadow-sm"
    >
      <h2 className="text-lg font-semibold text-neutral-900">New webhook</h2>
      <Input
        label="URL"
        type="url"
        required
        placeholder="https://your-app.test/webhooks/email-builder"
        value={url}
        onChange={(e) => setUrl(e.target.value)}
      />
      <Input
        label="Description"
        maxLength={200}
        value={description}
        onChange={(e) => setDescription(e.target.value)}
      />
      <fieldset>
        <legend className="text-sm font-medium text-neutral-700">Events</legend>
        <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-2">
          {WEBHOOK_EVENT_TYPES.map((event) => (
            <label key={event} className="flex items-start gap-2 text-sm text-neutral-700">
              <input
                type="checkbox"
                className="mt-0.5 h-4 w-4 rounded border-neutral-300"
                checked={events.includes(event)}
                onChange={() => toggleEvent(event)}
              />
              <code className="font-mono text-xs">{event}</code>
            </label>
          ))}
        </div>
      </fieldset>
      {error !== null && (
        <p
          role="alert"
          className="rounded-md border border-error-200 bg-error-50 px-3 py-2 text-sm text-error-700"
        >
          {error}
        </p>
      )}
      <div className="flex flex-wrap gap-2">
        <Button type="submit" variant="primary" loading={mutation.isPending}>
          Create webhook
        </Button>
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </form>
  );
}

function CreatedWebhookBanner({
  created,
  onClose,
}: {
  created: CreatedWebhook;
  onClose: () => void;
}): JSX.Element {
  return (
    <div className="rounded-lg border border-success-200 bg-success-50 p-6 shadow-sm">
      <h2 className="text-lg font-semibold text-success-700">
        Webhook created — copy the signing secret
      </h2>
      <p className="mt-1 text-sm text-success-700">
        Use this secret to verify HMAC-SHA256 signatures on incoming
        deliveries. We&apos;ll never show it again.
      </p>
      <pre className="mt-4 overflow-x-auto rounded border border-success-300 bg-white p-3 font-mono text-xs text-neutral-900">
        {created.secret}
      </pre>
      <div className="mt-4 flex gap-2">
        <Button
          variant="primary"
          onClick={() => {
            void navigator.clipboard.writeText(created.secret);
          }}
        >
          Copy secret
        </Button>
        <Button variant="outline" onClick={onClose}>
          I&apos;ve saved it
        </Button>
      </div>
    </div>
  );
}
